/* eslint-disable max-lines-per-function */
import { noop } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';

import { MedicalInformation, MedicalInformationType } from '@dreryk/egabinet-patient-medical-information-frontend-api';

import { commonTranslationKeys, useFormController } from '@dreryk/react-common';
import { LoaderSpinner, Message, MessageType, ModalConfirmButton } from '@dreryk/react-components';

import { patientMedicalInformationTranslationKeys } from '../../translations';

import { MedicalInformationTypeField } from './fields/MedicalInformationTypeField';
import { MedicalInformationAddEntryModalExitButtons } from './MedicalInformationAddEntryModal.buttons';
import {
   useMedicalInformationCreateDraftAction,
   useMedicalInformationRemoveDraftAction,
} from './MedicalInformationTypeSelector.actions';
import { findFirstAvailableMedicalInformationType } from './MedicalInformationTypeSelector.utils';

type MedicalInformationCategorySelectorFormBean = {
   medicalInformationType: MedicalInformationType | null;
};

export type MedicalInformationTypeSelectorRenderProps = {
   medicalInformationType?: MedicalInformationType;
   medicalInformationDraft?: MedicalInformation;
   onClose: VoidFunction;
};

export type MedicalInformationFormRenderer = (renderProps: MedicalInformationTypeSelectorRenderProps) => JSX.Element;

type MedicalInformationTypeSelectorProps = {
   readonly patientId: number;
   readonly availableMedicalInformationTypes: MedicalInformationType[] | undefined;
   readonly onClose: VoidFunction;
   readonly children: MedicalInformationFormRenderer;
};

export const MedicalInformationTypeSelector = (props: MedicalInformationTypeSelectorProps): JSX.Element | null => {
   const { register, watch, formState, trigger } = useFormController<MedicalInformationCategorySelectorFormBean>({
      medicalInformationType: findFirstAvailableMedicalInformationType(props.availableMedicalInformationTypes),
   });
   const { executeAsync: removeMedicalInformationDraft } = useMedicalInformationRemoveDraftAction();

   const medicalInformationType = watch('medicalInformationType');

   const {
      data: medicalInformationDraft,
      loading: medicalInformationDraftLoading,
      error: medicalInformationDraftError,
      executeAsync: createMedicalInformationDraft,
   } = useMedicalInformationCreateDraftAction({
      patientId: props.patientId,
      medicalInformationType: medicalInformationType,
   });

   useEffect(() => {
      if (medicalInformationType) {
         createMedicalInformationDraft();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [medicalInformationType]);

   const removeDraftBeforeClose = useCallback((): void => {
      if (medicalInformationDraft?.id) {
         removeMedicalInformationDraft(medicalInformationDraft.id);
      }

      props.onClose();
   }, [medicalInformationDraft, props, removeMedicalInformationDraft]);

   const children = useMemo(() => {
      if (!medicalInformationType) {
         return (
            <>
               <ModalConfirmButton
                  label={patientMedicalInformationTranslationKeys.addEntryModal.addEntry}
                  onClick={trigger}
               />
               <MedicalInformationAddEntryModalExitButtons shouldConfirmExit={false} onClose={props.onClose} />
            </>
         );
      }

      if (medicalInformationDraftLoading) {
         return (
            <>
               <LoaderSpinner />
               <ModalConfirmButton
                  label={patientMedicalInformationTranslationKeys.addEntryModal.addEntry}
                  onClick={noop}
                  disabled
               />
               <MedicalInformationAddEntryModalExitButtons shouldConfirmExit={false} onClose={props.onClose} />
            </>
         );
      }

      if (medicalInformationDraftError) {
         return (
            <>
               <Message
                  testId={'fetchError'}
                  type={MessageType.NEGATIVE}
                  label={commonTranslationKeys.unexpectedErrorHasOccurred}
                  displayIndicator
               />
               <MedicalInformationAddEntryModalExitButtons shouldConfirmExit={false} onClose={props.onClose} />
            </>
         );
      }

      return props.children({
         medicalInformationType: medicalInformationType,
         medicalInformationDraft: medicalInformationDraft,
         onClose: removeDraftBeforeClose,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [
      formState.isDirty,
      props.onClose,
      medicalInformationDraft,
      medicalInformationDraftError,
      medicalInformationDraftLoading,
      medicalInformationType,
   ]);

   return (
      <>
         <MedicalInformationTypeField
            register={register}
            availableMedicalInformationTypes={props.availableMedicalInformationTypes}
         />
         {children}
      </>
   );
};
